import React from "react";

import { AlertTriangle, ShieldAlert, CheckCircle2 } from "lucide-react";

import Skeleton from "@/components/shared/Skeleton";
import { CHART } from "./primitives";
import { Button } from "@/components/ui/Button";

// Cross-family exception surface, pinned above Family 1 (added 2026-08-01).
// Pulls one trigger condition out of each family below rather than adding new
// metrics: SLA breach + named bottleneck stage (F2), reviewer outlier (F3), AI
// accuracy floor + confidence inversion (F4), worst quality slot (F5). A
// leader with thirty seconds reads this strip first; clicking a card jumps to
// that family's expanded detail section. No items firing is a normal, good
// "all clear" state — rendered explicitly, not left blank.

const SEVERITY_STYLE = {
  critical: {
    border: CHART.red,
    bg: "bg-tone-rose/5",
    hoverBg: "hover:bg-tone-rose/10",
    icon: ShieldAlert,
    iconColor: CHART.red,
  },
  warning: {
    border: CHART.amber,
    bg: "bg-tone-amber/5",
    hoverBg: "hover:bg-tone-amber/10",
    icon: AlertTriangle,
    iconColor: CHART.amber,
  },
};

const AttentionCard = ({ item, onJump }) => {
  const style = SEVERITY_STYLE[item.severity] ?? SEVERITY_STYLE.warning;
  const Icon = style.icon;
  return (
    <Button
      type="button"
      onClick={() => onJump?.(item.family)}
      className={`text-left rounded-xl p-3 border border-border-primary ${style.bg} ${style.hoverBg} transition-colors flex-1 min-w-[220px]`}
      style={{ borderLeftWidth: 4, borderLeftColor: style.border }}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={13} style={{ color: style.iconColor }} className="flex-shrink-0" />
        <p className="text-[12px] font-semibold text-text-primary leading-tight truncate">
          {item.title}
        </p>
      </div>
      <p className="text-[11px] text-text-secondary leading-snug">{item.detail}</p>
    </Button>
  );
};

const AttentionStrip = ({ data, loading, onJump }) => {
  const items = data?.items ?? [];

  if (loading) {
    return (
      <div className="flex gap-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-16 flex-1 rounded-xl" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-xl p-3 border border-border-primary bg-background-secondary flex items-center gap-2">
        <CheckCircle2 size={15} style={{ color: CHART.green }} className="flex-shrink-0" />
        <p className="text-[12px] text-text-secondary">
          All clear — nothing needs attention in this range.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-3">
      {items.map((item) => (
        <AttentionCard key={item.key} item={item} onJump={onJump} />
      ))}
    </div>
  );
};

export default AttentionStrip;
